import React from 'react'
import { Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import {
  CampaignsProgramsCreateTargetsInner,
  CampaignsProgramsGetDetailParticipantsInner,
} from 'generated/api'
import { ExtendedCampaignsProgram, useCampaign } from '../hooks/useCampaignCreation'
import dayjs from 'dayjs'

interface Props {
  data: CampaignsProgramsGetDetailParticipantsInner[]
  detailCampaign: ExtendedCampaignsProgram
}

interface GroupedTargets {
  [key: string]: CampaignsProgramsCreateTargetsInner[]
}

const CampaignDetailTable: React.FC<Props> = props => {
  const { target } = useCampaign()

  function groupTargetsByMonth(participants: CampaignsProgramsGetDetailParticipantsInner[]) {
    const groupedTargets: GroupedTargets = {}
    participants.forEach(participant => {
      participant.targets.forEach(target => {
        const monthYear = new Date(target.startTime).toISOString().substr(0, 7)
        if (!groupedTargets[monthYear]) {
          groupedTargets[monthYear] = []
        }
        const targetWithUserId = { ...target, userId: participant.userId }
        groupedTargets[monthYear].push(targetWithUserId)
      })
    })
    return groupedTargets
  }

  const groupedTargets = groupTargetsByMonth(props.data)

  const targetColumns = Object.keys(groupedTargets).map((monthYear, index) => {
    const targetsForMonth = groupedTargets[monthYear]
    const date = new Date(monthYear)

    return {
      key: `column_${monthYear}`,
      dataIndex: `column_${monthYear}`,
      title: (
        <div className="flex justify-center">
          <Typography.Text className="font-semibold text-gray-800">
            Target {date.toLocaleDateString('id-ID', { month: 'long' })}
          </Typography.Text>
        </div>
      ),
      children: [
        {
          title: (
            <div className="flex">
              <Typography.Text className="font-semibold text-gray-800 mx-2">
                Harian (Ton)
              </Typography.Text>
            </div>
          ),
          dataIndex: `daily_${monthYear}_${index}`,
          key: `daily_${monthYear}_${index}`,
          width: 150,
          render: (_: unknown, record: CampaignsProgramsGetDetailParticipantsInner) => {
            const dailyTarget = target(targetsForMonth, record, 'DAILY')
            return (
              <div className="flex flex-col" data-testid="date">
                <Typography.Text className="font-medium text-sm text-gray-800 mx-2">
                  {dailyTarget ? dailyTarget.targetFfbWeightTonne : ''}
                </Typography.Text>
              </div>
            )
          },
        },
        {
          title: (
            <div className="flex">
              <Typography.Text className="font-semibold text-gray-800 mx-2">
                Bulanan (Ton)
              </Typography.Text>
            </div>
          ),
          dataIndex: `monthly_${monthYear}_${index}`,
          key: `monthly_${monthYear}_${index}`,
          width: 150,
          render: (_: unknown, record: CampaignsProgramsGetDetailParticipantsInner) => {
            const monthlyTarget = target(targetsForMonth, record, 'MONTHLY')
            return (
              <div className="flex flex-col" data-testid="date">
                <Typography.Text className="font-medium text-sm text-gray-800 mx-2">
                  {monthlyTarget ? monthlyTarget.targetFfbWeightTonne : ''}
                </Typography.Text>
              </div>
            )
          },
        },
      ],
    }
  })

  const columnsOrder: ColumnsType<CampaignsProgramsGetDetailParticipantsInner> = [
    {
      title: (
        <div className="flex">
          <Typography.Text className="font-semibold text-gray-800 mx-2">Nama</Typography.Text>
        </div>
      ),
      dataIndex: 'userName',
      key: 'userName',
      width: '20%',
      fixed: 'left',
      render: (text, record) => (
        <div className="flex flex-col" data-testid="date">
          <Typography.Text className="font-medium text-sm text-gray-800 mx-2">
            {text}
          </Typography.Text>
          <Typography.Text className="font-normal text-xs text-gray-400 mx-2">
            {dayjs(record.createdAt).format('DD MMM YYYY, HH:mm')}
          </Typography.Text>
        </div>
      ),
    },
    ...targetColumns,
    {
      title: (
        <div className="flex">
          <Typography.Text className="font-semibold text-gray-800 mx-2">
            Target Triwulan (Ton)
          </Typography.Text>
        </div>
      ),
      dataIndex: 'quarterly',
      width: '20%',
      key: 'quarterly',
      fixed: 'right',
      render: (_, record) => {
        const quarterlyTarget = record.targets.find((t: { type: string }) => t.type === 'QUARTERLY')
        return (
          <div className="flex flex-col">
            <Typography.Text className="font-medium text-sm text-gray-800 mx-2">
              {quarterlyTarget ? quarterlyTarget.targetFfbWeightTonne : ''}
            </Typography.Text>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className="rounded-tl-lg rounded-tr-lg flex flex-row justify-between px-6 py-2 border-[1px] border-b-0 border-[#e5e5e7] border-solid">
        <Typography.Text
          className="font-semibold text-gray-800 text-base"
          data-testid="information-participant"
        >
          Informasi Peserta
        </Typography.Text>
        <div className="flex flex-row mx-2">
          <Typography.Text className="font-normal text-gray-800 text-sm mr-1">
            Jumlah Peserta:
          </Typography.Text>
          <Typography.Text className="font-semibold text-gray-800 text-sm">
            {props.data.length || '0'}
          </Typography.Text>
        </div>
      </div>
      <Table
        rowKey={record => record.userId}
        className="table-campaign"
        columns={columnsOrder}
        dataSource={props.data}
        bordered
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </>
  )
}

export default CampaignDetailTable
